import { useCallback, useState } from 'react';
import {
  GridToolbarContainer,
  GridRowEditStopReasons,
  GridTreeDataGroupingCell,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { FieldArray, useFormikContext } from 'formik';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, AddButtonUi, DatagridPremiumUi } from '../../Interface';
import TaskListDialog from '../TaskListDialog';
import { DependenceOption } from '../../../config/taskList';
import useParam from '../../../context/ParamContext';

const EditToolbar = (props) => {
  const { disabled, setIsDialogOpen, setRows } = props;
  const { values } = useFormikContext();

  const handleClick = () => {
    setRows(values.taskListTemplateItem);
    setIsDialogOpen(true);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const TaskListTemplateItem = ({ disabled, defaultField, autoCompleteOptions }) => {
  const { params } = useParam();
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rows, setRows] = useState([]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = () => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        renderCell: (params) => {
          return <GridTreeDataGroupingCell {...params} label="" />;
        },
      },
      {
        field: 'duration',
        headerName: 'Időintervallum (nap)',
        type: 'number',
      },
      {
        field: 'waitingTime',
        headerName: 'Várakozási idő (nap)',
        type: 'number',
      },
      {
        field: 'responsiblePersonId',
        headerName: 'Felelős',
        type: 'string',
        renderCell: (data) => {
          const findedRespPerson = autoCompleteOptions?.user.find(
            (item) => item.value === data.row?.responsiblePersonId
          );
          return findedRespPerson?.label;
        },
      },
      {
        field: 'userId',
        headerName: 'Tulajdonos',
        type: 'string',
        renderCell: (data) => {
          const findedUser = autoCompleteOptions?.user.find((item) => item.value === data.row?.userId);
          return findedUser?.label;
        },
      },
      {
        field: 'userGroupId',
        headerName: 'Felelősök (csoport)',
        type: 'string',
        renderCell: (data) => {
          const findedRespPerson = autoCompleteOptions?.userGroups.find((item) => item.value === data.row?.userGroupId);
          return findedRespPerson?.label;
        },
      },
      {
        field: 'dependence',
        headerName: 'Függőség',
        type: 'string',
        renderCell: (data) => {
          const foundedDep = DependenceOption?.find((item) => item.value === data.row?.dependence);
          return foundedDep?.label;
        },
      },
      {
        field: 'visOrder',
        type: 'string',
        sortable: true,
        headerName: 'Sorrend',
      },
      {
        field: 'construction',
        type: 'boolean',
        sortable: true,
        headerName: 'Kivitelezés',
      },
      {
        field: 'moreRespPerson',
        type: 'string',
        sortable: true,
        headerName: 'További felelősök',
        renderCell: (data) => {
          const ids = data.row?.taskListRespPerson?.map((respPerson) => respPerson.userId);

          return autoCompleteOptions?.user
            ?.filter((u) => ids?.includes(u.value))
            ?.map((u) => u.label)
            ?.join(', ');
        },
      },
      ...(params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y'
        ? [
            {
              field: 'taskRatio',
              headerName: 'Feladat arány',
              type: 'number',
              sortable: false,
            },
          ]
        : []),
    ]);
  };

  const getTreeDataPath = useCallback((row) => row.path, []);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg mb-5 px-3">
          <FieldArray name="taskListTemplateItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.taskListTemplateItem}
                getRowHeight={() => 'auto'}
                height={630}
                headerFilters={false}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.taskListTemplateItem ?? []}
                treeData
                getTreeDataPath={getTreeDataPath}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                slots={{
                  toolbar: EditToolbar,
                }}
                groupingColDef={{
                  headerName: '',
                  renderCell: () => null,
                  width: 1,
                  hideable: false,
                  sortable: false,
                  resizable: false,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    setRowModesModel,
                    setIsDialogOpen,
                    setRows,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
      <TaskListDialog
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        autoCompleteOptions={autoCompleteOptions}
        rows={rows}
        defaultField={defaultField}
      />
    </div>
  );
};

export default TaskListTemplateItem;
